import { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@mui/material";

import { useTranslation } from "react-i18next";

import { useListRulesRulesGetQuery } from "api";
import { RulesPermissionEnum } from "authorization/types";
import { TooltipWithLock } from "components/TooltipWithLock/TooltipWithLock";
import { useAuthenticatedUser } from "hooks/useAuthenticatedUser";
import { IZone } from "interfaces/zone.interface";
import { mapById } from "utils/mapById";

import { RulesList } from "./components/RulesList";
import { RuleDialog } from "./dialogs/RuleDialog";

type RulesScreenProps = {
  entityId: string;
  ruleEntityType: "subject" | "zone";
  zone: IZone;
  zones: IZone[];
};

export function RulesScreen({
  entityId,
  ruleEntityType,
  zone,
  zones,
}: RulesScreenProps) {
  const { t } = useTranslation();
  const { permissions } = useAuthenticatedUser();

  const [includeDisabledRules, setIncludeDisabledRules] = useState(false);
  const [showDialog, setShowDialog] = useState<string | null>(null);

  const { data: rulesData } = useListRulesRulesGetQuery({
    entityId: entityId,
  });

  const rules =
    rulesData?.data
      ?.filter(({ type }) => type !== "device.event")
      .filter((r) => r.enabled || includeDisabledRules) ?? [];

  const noUpdateAccess = !permissions.includes(
    RulesPermissionEnum["rule.create"],
  );

  return (
    <Box borderRadius={2} width={"100%"}>
      <Box display={"flex"} justifyContent={"flex-end"} marginBottom={2}>
        <FormControlLabel
          label={t("include_disabled_rules")}
          onChange={(_, val) => setIncludeDisabledRules(val)}
          control={<Checkbox checked={includeDisabledRules} />}
        />
      </Box>

      <RuleDialog
        key={showDialog}
        isOpen={showDialog !== null}
        defaultValues={rules.find((r) => r.id === showDialog) ?? null}
        entityId={entityId}
        onClose={() => setShowDialog(null)}
        disabled={!!showDialog}
        ruleEntityType={ruleEntityType}
      />

      {rules.length === 0 ? (
        <Box padding={2}>
          <Typography>{t("there_are_no_rules_to_display")}</Typography>
        </Box>
      ) : (
        <RulesList
          rules={rules}
          zoneId={zone.id}
          zoneEntity={mapById(zones)}
          viewRuleDetails={setShowDialog}
          ruleEntityType={ruleEntityType}
        />
      )}

      {noUpdateAccess ? (
        <TooltipWithLock
          title={t("you_do_not_have_permission_to_create_new_rule")}
        >
          <Button variant={"text"} onClick={() => {}}>
            {t("add_a_rule")}
          </Button>
        </TooltipWithLock>
      ) : (
        <Button variant={"text"} onClick={() => setShowDialog("")}>
          {t("add_a_rule")}
        </Button>
      )}
    </Box>
  );
}
